import { el } from '../core/dom.js';
import { formatDuration } from '../core/time.js';

export type TimerMode = 'calm' | 'fast';

export interface TimerStatusRingOptions {
  /** Episode start timestamp (ISO). */
  startedAt: string;
  /** Optional target wait ms. The ring fills as elapsed/target. */
  targetWaitMs?: number | undefined;
  /** Calm mode hides sub-second motion for reduced-motion users. */
  mode?: TimerMode;
  /** Force reduced motion. Defaults to system preference. */
  reducedMotion?: boolean;
}

export interface TimerStatusRingHandle {
  root: HTMLElement;
  /** Update display. Call from an interval. */
  tick: () => void;
  /** Stop and remove. */
  destroy: () => void;
  /** Returns ms elapsed since startedAt. */
  elapsedMs: () => number;
  /** True if elapsed >= target. Always false when no target. */
  reachedTarget: () => boolean;
}

/**
 * Renders an SVG ring that visualizes elapsed time vs an optional target.
 * Caller drives ticks; we own the DOM but not the timer cadence so multiple
 * timers can share one rAF/setInterval.
 */
export function createTimerStatusRing(opts: TimerStatusRingOptions): TimerStatusRingHandle {
  const reduced = opts.reducedMotion ?? matchMedia('(prefers-reduced-motion: reduce)').matches;
  const mode: TimerMode = opts.mode ?? (reduced ? 'calm' : 'fast');
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 120 120');
  svg.setAttribute('aria-hidden', 'true');
  const r = 50;
  const c = 2 * Math.PI * r;
  const track = document.createElementNS(ns, 'circle');
  track.setAttribute('class', 'track');
  track.setAttribute('cx', '60'); track.setAttribute('cy', '60'); track.setAttribute('r', String(r));
  track.setAttribute('stroke-width', '10');
  const fill = document.createElementNS(ns, 'circle');
  fill.setAttribute('class', 'fill');
  fill.setAttribute('cx', '60'); fill.setAttribute('cy', '60'); fill.setAttribute('r', String(r));
  fill.setAttribute('stroke-width', '10');
  fill.setAttribute('stroke-dasharray', String(c));
  fill.setAttribute('stroke-dashoffset', String(c));
  svg.appendChild(track); svg.appendChild(fill);

  const ring = el('div', {
    class: `target-ring timer-ring${mode === 'calm' ? ' calm' : ''}`,
    dataset: { role: 'timer-ring' },
  });
  ring.appendChild(svg);
  const value = el('div', { class: 'time timer-ring__value', dataset: { live: 'time' } }, '0:00');
  const label = el('div', { class: 'label timer-ring__label' }, opts.targetWaitMs ? 'elapsed' : 'waiting');
  // Polite live region; only updated at meaningful intervals (30s, 1m, 2m, 5m,
  // target reached) so screen readers don't get spammed every tick.
  const a11y = el('span', {
    class: 'visually-hidden',
    attrs: { role: 'status', 'aria-live': 'polite', 'aria-atomic': 'true' },
  }, 'Urge timer started.');
  const center = el('div', { class: 'center' }, value, label);
  ring.appendChild(center);
  ring.appendChild(a11y);

  const startedAtMs = new Date(opts.startedAt).getTime();
  const target = opts.targetWaitMs;

  function elapsed(): number { return Math.max(0, Date.now() - startedAtMs); }
  function reached(): boolean { return target !== undefined && elapsed() >= target; }
  const ANNOUNCE_THRESHOLDS_MS = [30_000, 60_000, 120_000, 300_000, 600_000, 1800_000];
  let lastAnnouncedThreshold = 0;
  let announcedReached = false;
  function tick(): void {
    const e = elapsed();
    if (target !== undefined) {
      const isReached = e >= target;
      const ratio = Math.min(1, e / target);
      fill.setAttribute('stroke-dashoffset', String(c * (1 - ratio)));
      ring.classList.toggle('reached', isReached);
      if (isReached) {
        // Overtime: show +m:ss.hh past target. Caller can hide if undesired.
        const over = e - target;
        value.textContent = `+${formatTime(over, mode)}`;
        label.textContent = 'bonus wait';
      } else {
        value.textContent = formatTime(e, mode);
        label.textContent = 'elapsed';
      }
      if (isReached && !announcedReached) {
        a11y.textContent = 'Target reached. Every extra second counts.';
        announcedReached = true;
      }
    } else {
      value.textContent = formatTime(e, mode);
      fill.setAttribute('stroke-dashoffset', '0');
    }
    // Threshold announcements must NOT overwrite a fresh "target reached"
    // message in the same tick — that's the higher-priority event.
    if (!announcedReached) {
      for (const t of ANNOUNCE_THRESHOLDS_MS) {
        if (e >= t && lastAnnouncedThreshold < t) {
          lastAnnouncedThreshold = t;
          a11y.textContent = `${formatTime(e, 'calm')} elapsed.`;
        }
      }
    }
  }
  tick();

  return {
    root: ring,
    tick,
    destroy: () => { ring.remove(); },
    elapsedMs: elapsed,
    reachedTarget: reached,
  };
}

function formatTime(ms: number, mode: TimerMode): string {
  if (mode === 'calm') {
    // Avoid sub-second flicker. Round to nearest 5 seconds for very short, then minutes.
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${Math.max(0, Math.round(s / 5) * 5)}s`;
    const m = Math.floor(s / 60);
    return `${m}m`;
  }
  // Fast mode: m:ss.hh per doc-20 spec.
  if (ms < 60_000) {
    const totalCs = Math.floor(ms / 10);
    const s = Math.floor(totalCs / 100);
    const hh = totalCs % 100;
    return `${s}.${hh.toString().padStart(2, '0')}s`;
  }
  const totalCs = Math.floor(ms / 10);
  const m = Math.floor(totalCs / 6000);
  const s = Math.floor((totalCs % 6000) / 100);
  const hh = totalCs % 100;
  if (ms >= 3600_000) {
    // Fall back to the standard duration format past an hour.
    return formatDuration(ms);
  }
  return `${m}:${s.toString().padStart(2, '0')}.${hh.toString().padStart(2, '0')}`;
}
